import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Landmark } from "lucide-react";
import parentApi from "@/lib/parentApi";
import ChatThread from "@/components/ChatThread";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function ParentOfficeChat() {
  const { companyId } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const backTo = sp.get("student") ? `/parent/student/${sp.get("student")}` : sp.get("client") ? `/parent/client/${sp.get("client")}` : "/parent";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: d } = await parentApi.get(`/parent/office/threads/${companyId}`);
      setData(d);
    } catch (e) {
      if (e.response?.status === 401) { nav("/parent/login"); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [companyId, nav]);

  useEffect(() => {
    if (!localStorage.getItem("vlog_parent_token")) { nav("/parent/login"); return; }
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load, nav]);

  const send = async (body, attachment) => {
    try {
      const { data: msg } = await parentApi.post(`/parent/office/threads/${companyId}`, { body, attachment: attachment || undefined });
      setData((d) => ({ ...d, messages: [...(d?.messages || []), msg] }));
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      return false;
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-slate-50" data-testid="parent-office-chat-page">
      <header className="px-6 pt-2 pb-4 shrink-0">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm px-5 py-4 w-full flex items-center gap-3">
          <button onClick={() => nav(backTo)} data-testid="parent-office-back" className="h-9 w-9 rounded-lg border border-slate-200 bg-white text-slate-700 inline-flex items-center justify-center hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="h-9 w-9 rounded-lg bg-emerald-100 inline-flex items-center justify-center shrink-0">
            <Landmark className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold leading-tight truncate" data-testid="parent-office-school">{data?.school_name || "School office"}</div>
            <div className="text-[11px] text-slate-500 truncate">Direct line to the school administration</div>
          </div>
        </div>
      </header>
      <div className="flex-1 min-h-0 w-full px-6 pb-6 flex flex-col">
        <div className="flex-1 min-h-0 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        {loading ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <ChatThread
            messages={data?.messages || []}
            onSend={send}
            mySenderType="parent"
            emptyText="Ask the school office anything — billing, paperwork, schedules — a staff member will reply here."
          />
        )}
        </div>
      </div>
    </div>
  );
}
